import React, { useState } from 'react';
import { Calendar, Clock, User, Mail, Phone, MessageSquare, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { useToast } from '../hooks/use-toast';

const Appointment = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Appointment request:', formData);
    toast({
      title: "Appointment Request Submitted! ✨",
      description: "We'll contact you shortly to confirm your appointment.",
    });
    setFormData({
      name: '',
      email: '',
      phone: '',
      date: '',
      time: '',
      message: ''
    });
  };

  return (
    <section id="appointment" className="py-20 bg-gradient-to-br from-purple-50 via-pink-50 to-cyan-50 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-10 right-10 w-80 h-80 bg-gradient-to-bl from-purple-300/30 to-pink-300/30 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-10 left-10 w-96 h-96 bg-gradient-to-tr from-cyan-300/30 to-blue-300/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }}></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Sparkles className="h-6 w-6 text-purple-600 animate-pulse" />
            <span className="text-transparent bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text font-bold text-sm uppercase tracking-wider">Book Appointment</span>
            <Sparkles className="h-6 w-6 text-pink-600 animate-pulse" />
          </div>
          <h2 className="text-5xl font-bold bg-gradient-to-r from-gray-900 via-purple-800 to-pink-900 bg-clip-text text-transparent mt-2">Schedule Your Visit</h2>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-lg">
            Fill out the form below and we'll contact you to confirm your appointment
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          {/* Gradient border wrapper */}
          <div className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-500 rounded-3xl blur opacity-75 group-hover:opacity-100 transition duration-500"></div>
            <Card className="relative border-none shadow-2xl bg-white">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="flex items-center space-x-2 text-gray-700 font-semibold">
                        <User className="h-4 w-4 text-purple-600" />
                        <span>Full Name</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="border-2 border-purple-200 focus:border-purple-500 focus:ring-purple-500 transition-colors duration-300"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email" className="flex items-center space-x-2 text-gray-700 font-semibold">
                        <Mail className="h-4 w-4 text-cyan-600" />
                        <span>Email Address</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="border-2 border-cyan-200 focus:border-cyan-500 focus:ring-cyan-500 transition-colors duration-300"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="flex items-center space-x-2 text-gray-700 font-semibold">
                        <Phone className="h-4 w-4 text-blue-600" />
                        <span>Phone Number</span>
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="+1 (555) 123-4567"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="border-2 border-blue-200 focus:border-blue-500 focus:ring-blue-500 transition-colors duration-300"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="date" className="flex items-center space-x-2 text-gray-700 font-semibold">
                        <Calendar className="h-4 w-4 text-pink-600" />
                        <span>Preferred Date</span>
                      </Label>
                      <Input
                        id="date"
                        name="date"
                        type="date"
                        value={formData.date}
                        onChange={handleChange}
                        required
                        className="border-2 border-pink-200 focus:border-pink-500 focus:ring-pink-500 transition-colors duration-300"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="time" className="flex items-center space-x-2 text-gray-700 font-semibold">
                      <Clock className="h-4 w-4 text-indigo-600" />
                      <span>Preferred Time</span>
                    </Label>
                    <Input
                      id="time"
                      name="time"
                      type="time"
                      value={formData.time}
                      onChange={handleChange}
                      required
                      className="border-2 border-indigo-200 focus:border-indigo-500 focus:ring-indigo-500 transition-colors duration-300"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="flex items-center space-x-2 text-gray-700 font-semibold">
                      <MessageSquare className="h-4 w-4 text-teal-600" />
                      <span>Reason for Visit (Optional)</span>
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Please describe your symptoms or reason for visit..."
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="border-2 border-teal-200 focus:border-teal-500 focus:ring-teal-500 resize-none transition-colors duration-300"
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-500 hover:from-purple-600 hover:via-pink-600 hover:to-cyan-600 text-white shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 text-lg font-semibold"
                  >
                    <Calendar className="mr-2 h-6 w-6" />
                    Request Appointment
                    <Sparkles className="ml-2 h-5 w-5 animate-pulse" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Appointment;